import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import './DispatchMap.css';

// Fix default marker icon paths broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_COLORS = {
  OPEN:          '#94a3b8',
  ASSIGNED:      '#3b82f6',
  TRAVELING:     '#f59e0b',
  EN_ROUTE:      '#f59e0b',
  ARRIVED:       '#8b5cf6',
  DIAGNOSING:    '#ec4899',
  REPAIRING:     '#22c55e',
  WAITING_PARTS: '#ef4444',
  COMPLETED:     '#64748b',
  CANCELLED:     '#64748b',
};

function makeCallIcon(status) {
  const color = STATUS_COLORS[status] || '#94a3b8';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <path d="M16 0C7.2 0 0 7.2 0 16c0 11 16 24 16 24s16-13 16-24C32 7.2 24.8 0 16 0z"
        fill="${color}" stroke="white" stroke-width="2"/>
      <circle cx="16" cy="16" r="6" fill="white"/>
    </svg>`;
  return L.divIcon({
    className: '',
    html: svg,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });
}

function makeTechIcon(status) {
  const color = STATUS_COLORS[status] || '#3b82f6';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="16" fill="${color}" stroke="white" stroke-width="2.5"/>
      <text x="18" y="23" text-anchor="middle" fill="white" font-size="16" font-family="sans-serif">⚙</text>
    </svg>`;
  return L.divIcon({
    className: '',
    html: svg,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
  });
}

// Re-fit map bounds when markers change
function BoundsController({ points }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current || !points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [48, 48] });
    }
    fitted.current = true;
  }, [points, map]);

  return null;
}

function mapsUrl(lat, lng, address) {
  if (lat && lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  return null;
}

export default function DispatchMap({ calls, techLocations, onSelectCall, selectedCallId }) {
  const activeCalls = calls.filter(c =>
    c.status !== 'COMPLETED' && c.status !== 'CANCELLED' &&
    (c.destinationLatitude || c.roadServiceAddress)
  );
  const pinnedCalls = activeCalls.filter(c => c.destinationLatitude != null && c.destinationLongitude != null);
  const unpinnedCount = activeCalls.length - pinnedCalls.length;

  const techPoints = techLocations.map(t => [t.latitude, t.longitude]);
  const callPoints = pinnedCalls
    .map(c => [c.destinationLatitude, c.destinationLongitude]);

  const allPoints = [...techPoints, ...callPoints];

  // Default center: Albany NY area — adjust once real GPS data exists
  const defaultCenter = [42.6526, -73.7562];
  const defaultZoom = 10;

  return (
    <div className="dispatch-map">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        className="dispatch-map__canvas"
        zoomControl
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {allPoints.length > 0 && <BoundsController points={allPoints} />}

        {/* Road service call destination pins */}
        {pinnedCalls.map(call => {
          const navUrl = mapsUrl(call.destinationLatitude, call.destinationLongitude, call.roadServiceAddress);
          return (
            <Marker
              key={call.id}
              position={[call.destinationLatitude, call.destinationLongitude]}
              icon={makeCallIcon(call.status)}
              eventHandlers={{ click: () => onSelectCall?.(call) }}
            >
              <Popup>
                <div className="dispatch-map__popup">
                  <strong>{call.complaint}</strong>
                  <span className="dispatch-map__popup-status">{call.status}</span>
                  {call.roadServiceAddress && <small>{call.roadServiceAddress}</small>}
                  {navUrl && (
                    <a href={navUrl} target="_blank" rel="noreferrer" className="dispatch-map__popup-nav">
                      Open in Maps
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Technician live location pins */}
        {techLocations.map(loc => {
          const ageMs = Date.now() - (loc.timestamp * 1000);
          const stale = ageMs > 10 * 60 * 1000; // stale after 10 min
          return (
            <Marker
              key={loc.id}
              position={[loc.latitude, loc.longitude]}
              icon={makeTechIcon(loc.status)}
            >
              <Popup>
                <div className="dispatch-map__popup">
                  <strong>Tech #{loc.technicianId?.slice(-6) || '—'}</strong>
                  <span className="dispatch-map__popup-status">{loc.status || 'Unknown'}</span>
                  {stale && <small style={{ color: 'var(--tt-red)' }}>Location may be stale</small>}
                  <small>
                    Updated {Math.round(ageMs / 60000)} min ago
                  </small>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="dispatch-map__legend">
        {[
          { label: 'Open', color: STATUS_COLORS.OPEN },
          { label: 'Assigned', color: STATUS_COLORS.ASSIGNED },
          { label: 'Traveling', color: STATUS_COLORS.TRAVELING },
          { label: 'Arrived', color: STATUS_COLORS.ARRIVED },
          { label: 'Repairing', color: STATUS_COLORS.REPAIRING },
          { label: 'Parts', color: STATUS_COLORS.WAITING_PARTS },
        ].map(({ label, color }) => (
          <span key={label} className="dispatch-map__legend-item">
            <span className="dispatch-map__legend-dot" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>

      {unpinnedCount > 0 && (
        <div className="dispatch-map__notice">
          {unpinnedCount} active call{unpinnedCount !== 1 ? 's' : ''} need a better address to pin.
        </div>
      )}

      {activeCalls.length === 0 && techLocations.length === 0 && (
        <div className="dispatch-map__notice">
          No active road calls or tech GPS locations yet.
        </div>
      )}
    </div>
  );
}
